import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RotateCcw, Trophy, Undo2, AlertCircle, Check, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTheme } from '../../contexts/ThemeContext';
import ConfirmModal from '../../components/ConfirmModal';
import { badmintonGameAPI } from './services/badmintonGameApi';
import { getStatusToPhaseMapping } from '../phaseConfig';

const SHOT_TYPES = [
  { value: 'ace',           label: 'Ace',            category: 'winner' },
  { value: 'smash',         label: 'Smash',          category: 'winner' },
  { value: 'drop',          label: 'Drop',           category: 'winner' },
  { value: 'clear',         label: 'Clear',          category: 'winner' },
  { value: 'net_kill',      label: 'Net Kill',       category: 'winner' },
  { value: 'drive',         label: 'Drive',          category: 'winner' },
  { value: 'lift',          label: 'Lift',           category: 'winner' },
  { value: 'net_shot',      label: 'Net Shot',       category: 'winner' },
  { value: 'out',           label: 'Out',            category: 'error'  },
  { value: 'net_error',     label: 'Net Error',      category: 'error'  },
  { value: 'fault',         label: 'Fault',          category: 'error'  },
];

const SHOT_COLORS = {
  ace:           { active: 'bg-purple-600 text-white ring-2 ring-purple-300',   idle: 'bg-purple-100 text-purple-800 hover:bg-purple-200' },
  smash:         { active: 'bg-red-600 text-white ring-2 ring-red-300',         idle: 'bg-red-100 text-red-800 hover:bg-red-200' },
  drop:          { active: 'bg-teal-600 text-white ring-2 ring-teal-300',       idle: 'bg-teal-100 text-teal-800 hover:bg-teal-200' },
  clear:         { active: 'bg-sky-600 text-white ring-2 ring-sky-300',         idle: 'bg-sky-100 text-sky-800 hover:bg-sky-200' },
  net_kill:      { active: 'bg-orange-600 text-white ring-2 ring-orange-300',   idle: 'bg-orange-100 text-orange-800 hover:bg-orange-200' },
  drive:         { active: 'bg-indigo-600 text-white ring-2 ring-indigo-300',   idle: 'bg-indigo-100 text-indigo-800 hover:bg-indigo-200' },
  lift:          { active: 'bg-emerald-600 text-white ring-2 ring-emerald-300', idle: 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200' },
  net_shot:      { active: 'bg-purple-600 text-white ring-2 ring-purple-300',   idle: 'bg-purple-100 text-purple-800 hover:bg-purple-200' },
  out:           { active: 'bg-amber-600 text-white ring-2 ring-amber-300',     idle: 'bg-amber-100 text-amber-800 hover:bg-amber-200' },
  net_error:     { active: 'bg-orange-600 text-white ring-2 ring-orange-300',   idle: 'bg-orange-100 text-orange-800 hover:bg-orange-200' },
  service_fault: { active: 'bg-rose-600 text-white ring-2 ring-rose-300',       idle: 'bg-rose-100 text-rose-800 hover:bg-rose-200' },
  fault:         { active: 'bg-gray-600 text-white ring-2 ring-gray-300',       idle: 'bg-gray-200 text-gray-700 hover:bg-gray-300' },
};

const BadmintonScoring = ({
  gameCommonId,
  gameSettings,
  participantAInfo,
  participantBInfo,
  onGameComplete,
  onBack,
  onGamePointsStatus,
  event,
  isScorer = true,
  paused = false,
}) => {
  const { theme } = useTheme();
  const isModularFlow = !!gameCommonId;

  const [currentPhase, setCurrentPhase] = useState('scoring');
  const STATUS_TO_PHASE = getStatusToPhaseMapping('badminton');

  const [game, setGame] = useState(null);
  const [gameId, setGameId] = useState(gameCommonId || null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false });

  const [detailedScoring, setDetailedScoring] = useState(false);
  const [pendingWinner, setPendingWinner] = useState(null);
  const [selectedShotType, setSelectedShotType] = useState(null);
  const [scoredByPlayerId, setScoredByPlayerId] = useState(null);

  const [serveChosen, setServeChosen] = useState(false);
  const [completedGameSummary, setCompletedGameSummary] = useState(null);

  const initializingRef = useRef(false);

  useEffect(() => {
    const loadGame = async () => {
      if (!isModularFlow || !gameCommonId) return;
      try {
        setLoading(true);
        const existingGame = await badmintonGameAPI.getById(gameCommonId);
        setGame(existingGame);
        setGameId(existingGame.id);

        const isDoublesGame = (existingGame.playerA?.players?.length || 0) > 1;
        const serverKnown = !!existingGame.servingPlayer;
        const playerIdKnown = !isDoublesGame || existingGame.servingPlayerId != null;
        const currentGameHasPoints =
          (existingGame.currentGameScoreA || 0) + (existingGame.currentGameScoreB || 0) > 0;

        if (currentGameHasPoints || (serverKnown && playerIdKnown)) {
          setServeChosen(true);
        }

        if (onGamePointsStatus) onGamePointsStatus((existingGame.rallies?.length || 0) > 0);

        if (existingGame.status === 'completed') {
          setCurrentPhase('completed');
        } else {
          setCurrentPhase('scoring');
        }
      } catch (err) {
        console.error('[BadmintonScoring] Failed to load game:', err);
        setError('Failed to load game. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadGame();
  }, [isModularFlow, gameCommonId]);

  const isMatchComplete = useCallback(() => {
    if (!game) return false;
    const { currentGameScoreA, currentGameScoreB, maxPoints, isInSetting } = game;
    const settingCap = game.settingCap || 30;

    if (currentGameScoreA >= settingCap - 1 && currentGameScoreB >= settingCap - 1) {
      return Math.max(currentGameScoreA, currentGameScoreB) >= settingCap;
    }

    if (isInSetting) {
      return (currentGameScoreA >= maxPoints && currentGameScoreA - currentGameScoreB >= 2) ||
             (currentGameScoreB >= maxPoints && currentGameScoreB - currentGameScoreA >= 2);
    }

    return currentGameScoreA >= maxPoints || currentGameScoreB >= maxPoints;
  }, [game]);

  const isSeriesComplete = useCallback(() => {
    if (!game) return false;
    const gamesToWin = Math.ceil(game.bestOf / 2);
    return (game.playerAGamesWon || 0) >= gamesToWin || (game.playerBGamesWon || 0) >= gamesToWin;
  }, [game]);

  const getPlayerName = useCallback((playerId) => {
    if (!playerId || !game) return null;
    const id = parseInt(playerId, 10);
    if (parseInt(game.playerA?.id, 10) === id) return game.playerA?.name;
    if (parseInt(game.playerB?.id, 10) === id) return game.playerB?.name;
    for (const p of (game.playerA?.players || [])) {
      if (parseInt(p.id, 10) === id) return p.name;
    }
    for (const p of (game.playerB?.players || [])) {
      if (parseInt(p.id, 10) === id) return p.name;
    }
    return null;
  }, [game]);

  const resetDetailState = useCallback(() => {
    setPendingWinner(null);
    setSelectedShotType(null);
    setScoredByPlayerId(null);
  }, []);

  const handleShotType = useCallback(async (winningShot, errorShot, winningPlayerId, errorPlayerId) => {
    if (!game || !gameId || isMatchComplete() || saving) return;

    const shotType = winningShot || errorShot || null;

    const allPlayersA = game.playerA?.players?.length ? game.playerA.players : [{ id: game.playerA?.id }];
    const allPlayersB = game.playerB?.players?.length ? game.playerB.players : [{ id: game.playerB?.id }];
    const onTeamA = (id) => id != null && allPlayersA.some(p => String(p.id) === String(id));
    const onTeamB = (id) => id != null && allPlayersB.some(p => String(p.id) === String(id));

    let pointWinner;
    if (errorShot && errorPlayerId != null) {
      // Winner is always the opposite team of whoever made the error
      pointWinner = onTeamA(errorPlayerId) ? 'B' : 'A';
    } else if (errorShot === 'service_fault') {
      // Service fault with no specific player: winning team is opposite of server
      pointWinner = game.servingPlayer === 'A' ? 'B' : 'A';
    } else if (winningPlayerId != null) {
      pointWinner = onTeamA(winningPlayerId) ? 'A' : 'B';
    } else {
      pointWinner = pendingWinner;
    }

    if (!pointWinner) return;

    setSaving(true);
    setError(null);
    resetDetailState();

    try {
      const result = await badmintonGameAPI.recordRally(gameId, {
        pointWinner,
        ...(shotType && { shotType }),
        ...(winningPlayerId != null && { pointWinnerPlayerId: winningPlayerId }),
        ...(errorPlayerId != null && { pointLoserPlayerId: errorPlayerId }),
      });
      setGame(result.game);
      if ((game.rallies?.length || 0) === 0 && onGamePointsStatus) onGamePointsStatus(true);
    } catch (err) {
      console.error('[BadmintonScoring] Failed to record point:', err);
      setError('Failed to save point. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [game, gameId, isMatchComplete, saving, pendingWinner, resetDetailState, onGamePointsStatus]);

  const handleUndo = useCallback(async () => {
    if (!game || !gameId || saving) return;
    const hasCurrentRallies = (game.currentGameScoreA || 0) + (game.currentGameScoreB || 0) > 0;
    if (!hasCurrentRallies) return;

    setSaving(true);
    setError(null);

    try {
      const updatedGame = await badmintonGameAPI.undoLastRally(gameId);
      setGame(updatedGame);
      if ((game.rallies?.length || 0) === 1 && onGamePointsStatus) onGamePointsStatus(false);
    } catch (err) {
      console.error('[BadmintonScoring] Failed to undo:', err);
      setError('Failed to undo. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [game, gameId, saving, onGamePointsStatus]);

  const handleCompleteGame = useCallback(async () => {
    if (!game || !gameId || !isMatchComplete() || saving) return;

    const capturedGameNumber = game.currentMatch;
    const capturedScoreA = game.currentGameScoreA;
    const capturedScoreB = game.currentGameScoreB;
    const capturedWinnerSide = capturedScoreA > capturedScoreB ? 'A' : 'B';

    setSaving(true);
    setError(null);

    try {
      const response = await badmintonGameAPI.completeGame(gameId, { winnerPlayer: capturedWinnerSide });
      const updatedGame = response.game || response;
      setGame(updatedGame);

      if (updatedGame.status === 'completed') {
        setCurrentPhase('completed');
      } else {
        setCompletedGameSummary({
          gameNumber: capturedGameNumber,
          scoreA: capturedScoreA,
          scoreB: capturedScoreB,
          winnerSide: capturedWinnerSide,
          seriesA: updatedGame.playerAGamesWon || 0,
          seriesB: updatedGame.playerBGamesWon || 0,
        });
        setServeChosen(false);
        resetDetailState();
      }
    } catch (err) {
      console.error('[BadmintonScoring] Failed to complete game:', err);
      setError('Failed to complete game. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [game, gameId, isMatchComplete, saving, resetDetailState]);

  const handleCompleteEvent = async () => {
    if (!game) return;

    const playerAWon = (game.playerAGamesWon || 0) > (game.playerBGamesWon || 0);
    const winner = playerAWon ? game.playerA : game.playerB;
    const winnerType = winner?.type || 'user';
    const winnerId = winner?.id;

    const result = {
      winnerId,
      winnerType,
      scoreSummary: `${game.playerAGamesWon || 0}-${game.playerBGamesWon || 0}`,
      playerAScore: game.playerAGamesWon || 0,
      playerBScore: game.playerBGamesWon || 0,
      winner,
      gameId: game.id
    };

    if (onGameComplete) {
      await onGameComplete(result);
    }
  };


  const handleRefresh = async () => {
    if (!gameId) return;
    try {
      setSaving(true);
      const refreshed = await badmintonGameAPI.getById(gameId);
      setGame(refreshed);
      if (refreshed.status === 'completed') setCurrentPhase('completed');
      toast.success('Score refreshed');
    } catch (err) {
      console.error('[BadmintonScoring] Failed to refresh:', err);
      toast.error('Failed to refresh score');
    } finally {
      setSaving(false);
    }
  };

  const handleResetMatch = () => {
    setConfirmModal({
      isOpen: true,
      title: 'Reset Match',
      message: 'Are you sure you want to reset this match? All rallies will be deleted and scoring will restart from zero.',
      confirmText: 'Reset',
      onConfirm: async () => {
        setConfirmModal({ isOpen: false });
        try {
          const resetGame = await badmintonGameAPI.resetGame(gameId);
          setGame(resetGame);
          setCurrentPhase('scoring');
          setServeChosen(false);
          setCompletedGameSummary(null);
          toast.success('Match reset — ready to start scoring again');
        } catch (err) {
          console.error('[BadmintonScoring] Failed to reset game:', err);
          toast.error('Failed to reset match');
        }
      },
      onCancel: () => setConfirmModal({ isOpen: false })
    });
  };


  const handleServeSelected = useCallback(async (servingPlayer, servingPlayerId) => {
    try {
      setSaving(true);
      setError(null);
      const updatedGame = await badmintonGameAPI.setInitialServe(gameId, servingPlayer, servingPlayerId);
      setGame(updatedGame);
      setServeChosen(true);
    } catch (err) {
      console.error('[BadmintonScoring] Failed to set serving player:', err);
      setError('Failed to set serving player. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [gameId]);

  // ─── Rally history ────────────────────────────────────────────────────────────

  const renderRallyHeaders = () => (
    <div className={`grid grid-cols-4 text-xs ${theme.textMuted} py-1 border-b ${theme.border} mb-1`}>
      <span>Point</span>
      <span className="text-center">Scored by</span>
      <span className="text-center">Error by</span>
      <span className="text-right pr-2">Score</span>
    </div>
  );

  const renderRallyRows = (rallies) => {
    const sorted = [...rallies].reverse();
    const rows = [];
    let lastGameNumber = null;

    sorted.forEach((rally) => {
      if (rally.gameNumber !== lastGameNumber) {
        lastGameNumber = rally.gameNumber;
        rows.push(
          <div
            key={`game-header-${rally.gameNumber}`}
            className={`text-xs font-semibold ${theme.textMuted} py-1 mt-1 border-b ${theme.border}`}
          >
            Game {rally.gameNumber}
          </div>
        );
      }

      const scorerIsA  = rally.pointWinner === 'A';
      const newScoreA  = rally.playerAScoreBefore + (scorerIsA ? 1 : 0);
      const newScoreB  = rally.playerBScoreBefore + (!scorerIsA ? 1 : 0);
      const isError    = ['out', 'net_error', 'service_fault', 'fault'].includes(rally.shotType);
      const winningTeamName = scorerIsA ? game.playerA?.name : game.playerB?.name;
      const scorerName = getPlayerName(rally.pointWinnerPlayerId) || winningTeamName;
      const loserName  = getPlayerName(rally.pointLoserPlayerId);

      rows.push(
        <div key={rally.id} className={`grid grid-cols-4 text-xs py-1.5 border-b ${theme.border} last:border-0 items-center`}>
          <span className={theme.textMuted}>#{rally.rallySequence}</span>
          <span className={`text-center truncate ${scorerIsA ? 'text-green-600' : 'text-blue-600'}`}>
            {scorerName}
            {rally.shotType && !isError && (
              <span className="ml-1">({rally.shotType})</span>
            )}
          </span>
          <span className={`text-center truncate ${isError ? 'text-red-500' : theme.textMuted}`}>
            {isError ? (
              <>
                {loserName || '—'}
                {rally.shotType && (
                  <span className="ml-1">({rally.shotType.replace('_', ' ')})</span>
                )}
              </>
            ) : '—'}
          </span>
          <span className={`text-right pr-2 font-medium ${theme.text}`}>
            {newScoreA} - {newScoreB}
          </span>
        </div>
      );
    });

    return rows;
  };

  // ─── Between-games screen ─────────────────────────────────────────────────────

  const renderBetweenGamesScreen = () => {
    if (!completedGameSummary || !game) return null;
    const { gameNumber, scoreA, scoreB, winnerSide, seriesA, seriesB } = completedGameSummary;
    const winnerName = winnerSide === 'A' ? game.playerA?.name : game.playerB?.name;
    const nextGameNumber = gameNumber + 1;

    return (
      <div className="space-y-4">
        <div className={`${theme.card} rounded-lg border ${theme.border} p-6 text-center`}>
          <Trophy className="w-12 h-12 mx-auto text-yellow-500 mb-3" />
          <div className={`text-lg font-bold ${theme.text} mb-2`}>Game {gameNumber} Complete</div>
          <div className="text-3xl font-bold mb-2">
            <span className={winnerSide === 'A' ? 'text-green-500' : theme.text}>{scoreA}</span>
            <span className={`mx-2 ${theme.textMuted}`}>-</span>
            <span className={winnerSide === 'B' ? 'text-green-500' : theme.text}>{scoreB}</span>
          </div>
          <div className={`text-sm ${theme.textMuted} mb-4`}>{winnerName} won this game</div>
          <div className={`text-base font-semibold ${theme.text}`}>
            Series: {game.playerA?.name}{' '}
            <span className="text-green-500">{seriesA}</span>
            {' — '}
            <span className="text-green-500">{seriesB}</span>
            {' '}{game.playerB?.name}
          </div>
        </div>

        <div className={`${theme.card} rounded-lg border ${theme.border} p-4 text-center`}>
          <div className={`text-sm ${theme.textMuted}`}>
            Ready for Game {nextGameNumber} of {game.bestOf}
          </div>
          <div className={`text-xs ${theme.textMuted} mt-1`}>
            You'll select the first server after starting
          </div>
        </div>

        <button
          onClick={() => setCompletedGameSummary(null)}
          className={`w-full ${theme.accent} ${theme.accentHover} py-4 rounded-lg font-semibold text-lg flex items-center justify-center gap-2`}
        >
          <Trophy className="w-5 h-5" />
          Start Game {nextGameNumber}
        </button>
      </div>
    );
  };

  // ─── Scoring Phase ────────────────────────────────────────────────────────────

  const renderScoringPhase = () => {
    if (!game) return null;

    const matchComplete = isMatchComplete();
    const ralliesCount = game.rallies?.length || 0;
    const isDoubles = (game.playerA?.players?.length || 0) > 1;
    const hasCurrentGameRallies = (game.currentGameScoreA || 0) + (game.currentGameScoreB || 0) > 0;

    const teamAPlayers = isDoubles ? (game.playerA?.players || []) : [];
    const teamBPlayers = isDoubles ? (game.playerB?.players || []) : [];

    const getPlayerPosition = (playerId, evenPlayerId, teamScore) => {
      if (!evenPlayerId) return null;
      const isEvenPlayer = parseInt(playerId) === parseInt(evenPlayerId);
      const scoreIsEven = (teamScore ?? 0) % 2 === 0;
      if (scoreIsEven) return isEvenPlayer ? 'R' : 'L';
      return isEvenPlayer ? 'L' : 'R';
    };

    const isErrorShot = ['out', 'net_error', 'service_fault', 'fault'].includes(selectedShotType);

    // Require server pick if doubles and serve team is known but specific player isn't
    const needsServerPick = isDoubles && serveChosen && !game.servingPlayerId && !matchComplete;
    const servingTeamName = game.servingPlayer === 'A' ? game.playerA?.name : game.playerB?.name;
    const servingTeamPlayers = game.servingPlayer === 'A' ? teamAPlayers : teamBPlayers;

    return (
      <div className="space-y-4">
        {/* Game Header */}
        <div className={`${theme.card} rounded-lg border ${theme.border} p-4`}>
          <div className="flex items-center justify-between">
            <div className="flex-1 text-center">
              {game.bestOf > 1 && (
                <div className={`text-sm ${theme.textMuted} mb-1`}>
                  Game {game.currentMatch} of {game.bestOf}
                </div>
              )}
              <div className={`text-lg font-semibold ${theme.text}`}>
                {game.playerA?.name} vs {game.playerB?.name}
              </div>
              {game.bestOf > 1 && (
                <div className={`text-sm ${theme.textMuted} mt-1`}>
                  Series: {game.playerAGamesWon || 0} — {game.playerBGamesWon || 0}
                </div>
              )}
            </div>
          </div>

          {game.isInSetting && !matchComplete && (
            <div className="flex justify-center mt-2">
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                SETTING — next 2-point lead wins
              </span>
            </div>
          )}

          {!paused && (
            <div className="flex justify-center mt-2">
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                LIVE
              </span>
            </div>
          )}
        </div>

        {/* Initial serve selection */}
        {isScorer && !serveChosen && (
          <div className={`${theme.card} rounded-lg border-2 ${theme.border} p-5 space-y-4`}>
            <div className="text-center">
              <div className={`text-base font-semibold ${theme.text} mb-1`}>Who serves first?</div>
              <div className={`text-sm ${theme.textMuted}`}>Select the player that starts serving</div>
            </div>

            {isDoubles ? (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <div className={`text-xs font-medium text-center ${theme.textMuted} mb-1`}>{game.playerA?.name}</div>
                  {teamAPlayers.filter(p => p).map(player => (
                    <button
                      key={player.id}
                      disabled={saving || paused}
                      onClick={() => handleServeSelected('A', player.id)}
                      className={`w-full py-3 rounded-lg font-medium text-sm border-2 transition-all active:scale-95 disabled:opacity-50 ${theme.border} ${theme.text} hover:border-green-400 hover:bg-green-500/20 hover:text-green-400`}
                    >
                      {player.name}
                    </button>
                  ))}
                </div>
                <div className="space-y-2">
                  <div className={`text-xs font-medium text-center ${theme.textMuted} mb-1`}>{game.playerB?.name}</div>
                  {teamBPlayers.filter(p => p).map(player => (
                    <button
                      key={player.id}
                      disabled={saving || paused}
                      onClick={() => handleServeSelected('B', player.id)}
                      className={`w-full py-3 rounded-lg font-medium text-sm border-2 transition-all active:scale-95 disabled:opacity-50 ${theme.border} ${theme.text} hover:border-blue-400 hover:bg-blue-500/20 hover:text-blue-400`}
                    >
                      {player.name}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <button
                  disabled={saving || paused}
                  onClick={() => handleServeSelected('A', game.playerA?.id)}
                  className={`w-full py-4 rounded-lg font-semibold text-sm border-2 transition-all active:scale-95 disabled:opacity-50 ${theme.border} ${theme.text} hover:border-green-400 hover:bg-green-500/20 hover:text-green-400`}
                >
                  {game.playerA?.name}
                </button>
                <button
                  disabled={saving || paused}
                  onClick={() => handleServeSelected('B', game.playerB?.id)}
                  className={`w-full py-4 rounded-lg font-semibold text-sm border-2 transition-all active:scale-95 disabled:opacity-50 ${theme.border} ${theme.text} hover:border-blue-400 hover:bg-blue-500/20 hover:text-blue-400`}
                >
                  {game.playerB?.name}
                </button>
              </div>
            )}

            {saving && (
              <div className={`text-center text-sm ${theme.textMuted} flex items-center justify-center gap-2`}>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent" />
                Saving...
              </div>
            )}
          </div>
        )}

        {/* Mid-game serve prompt for doubles (serve changed to team that hasn't served yet) */}
        {isScorer && needsServerPick && (
          <div className={`${theme.card} rounded-lg border-2 border-yellow-400 p-4 space-y-3`}>
            <div className="text-center">
              <div className={`text-sm font-semibold ${theme.text}`}>
                Who is serving for {servingTeamName}?
              </div>
              <div className={`text-xs ${theme.textMuted} mt-0.5`}>Select the server to continue</div>
            </div>
            <div className="grid gap-2">
              {servingTeamPlayers.filter(p => p).map(player => (
                <button
                  key={player.id}
                  disabled={saving || paused}
                  onClick={() => handleServeSelected(game.servingPlayer, player.id)}
                  className="w-full py-2.5 rounded-lg font-medium text-sm border-2 transition-all active:scale-95 disabled:opacity-50 border-yellow-400 text-yellow-600 hover:bg-yellow-400/20"
                >
                  {player.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Score Cards — display only */}
        <div className="grid grid-cols-2 gap-4">
          <div className={`${theme.card} rounded-lg border ${theme.border} p-4 text-center`}>
            <div className={`text-6xl font-bold ${theme.text} mb-1`}>{game.currentGameScoreA}</div>
            {game.bestOf > 1 && (
              <div className={`text-xs ${theme.textMuted} mb-1`}>Games: {game.playerAGamesWon || 0}</div>
            )}
            <div className="flex justify-center mt-1">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                game.servingPlayer === 'A' ? 'ring-2 ring-red-500 text-red-500' : theme.textMuted
              }`}>
                {game.servingPlayer === 'A' && (
                  <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse flex-shrink-0" />
                )}
                {game.playerA?.name}
              </span>
            </div>
            {game.servingPlayer === 'A' && isDoubles && game.servingPlayerId && (
              <div className={`text-xs ${theme.textMuted} mt-1`}>Serving: {getPlayerName(game.servingPlayerId)}</div>
            )}
          </div>

          <div className={`${theme.card} rounded-lg border ${theme.border} p-4 text-center`}>
            <div className={`text-6xl font-bold ${theme.text} mb-1`}>{game.currentGameScoreB}</div>
            {game.bestOf > 1 && (
              <div className={`text-xs ${theme.textMuted} mb-1`}>Games: {game.playerBGamesWon || 0}</div>
            )}
            <div className="flex justify-center mt-1">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                game.servingPlayer === 'B' ? 'ring-2 ring-red-500 text-red-500' : theme.textMuted
              }`}>
                {game.servingPlayer === 'B' && (
                  <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse flex-shrink-0" />
                )}
                {game.playerB?.name}
              </span>
            </div>
            {game.servingPlayer === 'B' && isDoubles && game.servingPlayerId && (
              <div className={`text-xs ${theme.textMuted} mt-1`}>Serving: {getPlayerName(game.servingPlayerId)}</div>
            )}
          </div>
        </div>

        {/* Server info bar — mirrors pickleball layout */}
        {serveChosen && game.servingPlayer && !needsServerPick && (
          <div className={`${theme.card} rounded-lg border ${theme.border} p-3 text-center text-sm ${theme.text}`}>
            Server:{' '}
            <span className="font-bold">
              {isDoubles && game.servingPlayerId
                ? getPlayerName(game.servingPlayerId)
                : (game.servingPlayer === 'A' ? game.playerA?.name : game.playerB?.name)
              }
            </span>
            {isDoubles && game.servingPlayerId && (
              <span className={theme.textMuted}>
                {' '}({game.servingPlayer === 'A' ? game.playerA?.name : game.playerB?.name})
              </span>
            )}
          </div>
        )}

        {/* Detailed Scoring Toggle — scorer only, after serve chosen */}
        {isScorer && serveChosen && !matchComplete && (
          <label className={`flex items-center gap-2 text-sm ${theme.textMuted} cursor-pointer select-none`}>
            <input
              type="checkbox"
              checked={detailedScoring}
              onChange={e => { setDetailedScoring(e.target.checked); resetDetailState(); }}
              className="rounded"
            />
            <span>Enable Detailed Scoring</span>
          </label>
        )}

        {/* Player score buttons — shown to all; interactive for scorer only */}
        {serveChosen && !matchComplete && !needsServerPick && (
          isDoubles ? (
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-3">
                <div className="text-xs font-semibold text-center uppercase tracking-wide text-green-500 truncate">
                  {game.playerA?.name}
                </div>
                {teamAPlayers.filter(p => p).map(player => {
                  const isServer = isDoubles && game.servingPlayerId && player.id === game.servingPlayerId;
                  const position = getPlayerPosition(player.id, game.evenPlayerIdA, game.currentGameScoreA);
                  return (
                    <button
                      key={player.id}
                      onClick={isScorer ? () => {
                        if (detailedScoring) {
                          setPendingWinner('A');
                          setScoredByPlayerId(player.id);
                          setSelectedShotType(null);
                        } else {
                          handleShotType(null, null, player.id, null);
                        }
                      } : undefined}
                      disabled={!isScorer || saving || paused}
                      className={`w-full bg-green-600 text-white py-5 rounded-lg font-semibold transition-transform ${isScorer ? 'hover:bg-green-700 active:bg-green-800 active:scale-95 disabled:opacity-50' : 'opacity-70 cursor-default'} ${isServer ? 'ring-2 ring-red-500 ring-offset-1 ring-offset-transparent' : ''}`}
                    >
                      {player.name}{position && <span className="ml-1.5 text-xs font-normal opacity-75">({position})</span>}
                    </button>
                  );
                })}
              </div>
              <div className="flex flex-col gap-3">
                <div className="text-xs font-semibold text-center uppercase tracking-wide text-blue-400 truncate">
                  {game.playerB?.name}
                </div>
                {teamBPlayers.filter(p => p).map(player => {
                  const isServer = isDoubles && game.servingPlayerId && player.id === game.servingPlayerId;
                  const position = getPlayerPosition(player.id, game.evenPlayerIdB, game.currentGameScoreB);
                  return (
                    <button
                      key={player.id}
                      onClick={isScorer ? () => {
                        if (detailedScoring) {
                          setPendingWinner('B');
                          setScoredByPlayerId(player.id);
                          setSelectedShotType(null);
                        } else {
                          handleShotType(null, null, player.id, null);
                        }
                      } : undefined}
                      disabled={!isScorer || saving || paused}
                      className={`w-full bg-blue-600 text-white py-5 rounded-lg font-semibold transition-transform ${isScorer ? 'hover:bg-blue-700 active:bg-blue-800 active:scale-95 disabled:opacity-50' : 'opacity-70 cursor-default'} ${isServer ? 'ring-2 ring-red-500 ring-offset-1 ring-offset-transparent' : ''}`}
                    >
                      {player.name}{position && <span className="ml-1.5 text-xs font-normal opacity-75">({position})</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={isScorer ? () => {
                  if (detailedScoring) {
                    setPendingWinner('A');
                    setScoredByPlayerId(game.playerA?.id);
                    setSelectedShotType(null);
                  } else {
                    handleShotType(null, null, game.playerA?.id, null);
                  }
                } : undefined}
                disabled={!isScorer || saving || paused}
                className={`bg-green-600 text-white py-6 rounded-lg transition-transform ${isScorer ? 'hover:bg-green-700 active:bg-green-800 active:scale-95 disabled:opacity-50' : 'opacity-70 cursor-default'} ${game.servingPlayer === 'A' ? 'ring-2 ring-red-500 ring-offset-1 ring-offset-transparent' : ''}`}
              >
                <div className="flex flex-col items-center gap-0.5">
                  <span className="font-semibold text-lg">{game.playerA?.name}</span>
                  {isScorer && <span className="text-xs font-normal opacity-70">tap to score</span>}
                </div>
              </button>
              <button
                onClick={isScorer ? () => {
                  if (detailedScoring) {
                    setPendingWinner('B');
                    setScoredByPlayerId(game.playerB?.id);
                    setSelectedShotType(null);
                  } else {
                    handleShotType(null, null, game.playerB?.id, null);
                  }
                } : undefined}
                disabled={!isScorer || saving || paused}
                className={`bg-blue-600 text-white py-6 rounded-lg transition-transform ${isScorer ? 'hover:bg-blue-700 active:bg-blue-800 active:scale-95 disabled:opacity-50' : 'opacity-70 cursor-default'} ${game.servingPlayer === 'B' ? 'ring-2 ring-red-500 ring-offset-1 ring-offset-transparent' : ''}`}
              >
                <div className="flex flex-col items-center gap-0.5">
                  <span className="font-semibold text-lg">{game.playerB?.name}</span>
                  {isScorer && <span className="text-xs font-normal opacity-70">tap to score</span>}
                </div>
              </button>
            </div>
          )
        )}

        {/* Detail scoring panel — shot types only, always visible when detail scoring on */}
        {serveChosen && detailedScoring && !matchComplete && (
          <div className={`${theme.card} rounded-lg border ${theme.border} p-4 space-y-4`}>

            {/* Header — only shown when a player has been tapped */}
            {pendingWinner != null && (
              <div className="flex items-center justify-between">
                <h3 className={`${theme.text} font-semibold`}>
                  {(() => {
                    if (isDoubles) {
                      const players = pendingWinner === 'A' ? teamAPlayers : teamBPlayers;
                      const scorer = scoredByPlayerId ? players.find(p => String(p.id) === String(scoredByPlayerId)) : null;
                      return scorer?.name || (pendingWinner === 'A' ? game.playerA?.name : game.playerB?.name);
                    }
                    return pendingWinner === 'A' ? game.playerA?.name : game.playerB?.name;
                  })()} Won Point
                </h3>
                <button
                  onClick={() => resetDetailState()}
                  className={`text-sm ${theme.textMuted} hover:${theme.text}`}
                >
                  Cancel
                </button>
              </div>
            )}

            {/* Winning shot type — tap records immediately */}
            <div>
              <div className={`text-xs font-semibold uppercase tracking-wide ${theme.textMuted} mb-1.5`}>Winning Shot Type</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {SHOT_TYPES.filter(s => s.category === 'winner').map(shot => {
                  const colors = SHOT_COLORS[shot.value];
                  const isAce = shot.value === 'ace';
                  const isAceBlocked = isAce && pendingWinner != null && pendingWinner !== game.servingPlayer;
                  const disabled = isAce ? (isAceBlocked || saving || paused) : (pendingWinner == null || saving || paused);
                  return (
                    <button
                      key={shot.value}
                      disabled={disabled}
                      onClick={() => {
                        if (isAce) {
                          handleShotType('ace', null, game.servingPlayerId, null);
                        } else {
                          handleShotType(shot.value, null, scoredByPlayerId, null);
                        }
                      }}
                      className={`py-2 px-2 rounded-lg text-sm font-semibold transition-all ${
                        disabled
                          ? 'opacity-40 cursor-not-allowed bg-gray-100 text-gray-400'
                          : colors.idle
                      }`}
                    >
                      {shot.label}
                      {isAceBlocked && <span className="block text-xs opacity-60">not server</span>}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Opponent error type — service fault always enabled; others need player selection */}
            <div>
              <div className={`text-xs font-semibold uppercase tracking-wide ${theme.textMuted} mb-1.5`}>Opponent Error Type</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {/* Service Fault — always enabled, awards point to receiving team */}
                <button
                  onClick={() => handleShotType(null, 'service_fault', null, game.servingPlayerId)}
                  disabled={saving || paused}
                  className="py-2 px-2 rounded-lg text-sm font-semibold transition-all bg-rose-100 text-rose-800 hover:bg-rose-200 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Service Fault
                </button>
                {SHOT_TYPES.filter(s => s.category === 'error').map(shot => {
                  const colors = SHOT_COLORS[shot.value];
                  return (
                    <button
                      key={shot.value}
                      disabled={pendingWinner == null || saving || paused}
                      onClick={() => {
                        if (!isDoubles) {
                          const opponentId = pendingWinner === 'A' ? game.playerB?.id : game.playerA?.id;
                          handleShotType(null, shot.value, null, opponentId);
                        } else {
                          setSelectedShotType(shot.value);
                        }
                      }}
                      className={`py-2 px-2 rounded-lg text-sm font-semibold transition-all ${
                        pendingWinner == null
                          ? 'opacity-40 cursor-not-allowed ' + colors.idle
                          : selectedShotType === shot.value ? colors.active : colors.idle
                      }`}
                    >
                      {shot.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Doubles: Who faulted? — tap records immediately */}
            {isDoubles && isErrorShot && (
              <div>
                <div className={`text-xs font-semibold uppercase tracking-wide ${theme.textMuted} mb-1.5`}>Who Faulted?</div>
                <div className="grid grid-cols-2 gap-2">
                  {(pendingWinner === 'A' ? teamBPlayers : teamAPlayers).filter(p => p).map(player => (
                    <button
                      key={player.id}
                      disabled={saving || paused}
                      onClick={() => handleShotType(null, selectedShotType, scoredByPlayerId, player.id)}
                      className="py-2 px-2 rounded-lg text-sm font-semibold transition-all active:scale-95 bg-red-100 text-red-800 hover:bg-red-200 disabled:opacity-50"
                    >
                      {player.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {saving && !detailedScoring && (
          <div className={`text-center text-sm ${theme.textMuted} flex items-center justify-center gap-2`}>
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent" />
            Saving...
          </div>
        )}

        {serveChosen && !needsServerPick && !matchComplete && !saving && !detailedScoring && (
          <p className={`text-center text-sm ${theme.textMuted}`}>
            {isScorer ? "Tap a score to add a point" : 'Watching live — scores update automatically'}
          </p>
        )}

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}

        {/* Action Buttons */}
        {isScorer && !paused && (
          <div>
            {matchComplete && (
              <>
                <button
                  onClick={handleCompleteGame}
                  disabled={saving || paused}
                  className={`w-full ${theme.accent} ${theme.accentHover} py-4 rounded-lg font-semibold text-lg flex items-center justify-center gap-2`}
                >
                  {saving ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                  ) : (
                    <>
                      <Trophy className="w-5 h-5" />
                      {isSeriesComplete() ? 'Complete Match' : `Complete Game ${game.currentMatch}`}
                    </>
                  )}
                </button>
                <div style={{ height: '16px' }} />
              </>
            )}
            <button
              onClick={handleUndo}
              disabled={!hasCurrentGameRallies || saving || paused}
              className={`w-full ${theme.secondary} ${theme.secondaryHover} ${theme.text} py-3 rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50`}
            >
              <Undo2 className="w-4 h-4" />
              Undo Last Point
            </button>
          </div>
        )}

        {/* Rally History */}
        {ralliesCount > 0 && (
          <details className={`${theme.card} rounded-lg border ${theme.border} mt-2`}>
            <summary className={`${theme.text} p-4 cursor-pointer font-medium`}>
              Point History ({ralliesCount} points)
            </summary>
            <div className="px-3 pb-3">
              {renderRallyHeaders()}
              <div className="max-h-40 overflow-y-auto">
                {renderRallyRows(game.rallies || [])}
              </div>
            </div>
          </details>
        )}
      </div>
    );
  };

  // ─── Completed Phase ──────────────────────────────────────────────────────────

  const renderCompletedPhase = () => {
    if (!game) return null;

    const playerAWon = (game.playerAGamesWon || 0) > (game.playerBGamesWon || 0);
    const winnerName = game.winner?.name || (playerAWon ? game.playerA?.name : game.playerB?.name);
    const loserName = playerAWon ? game.playerB?.name : game.playerA?.name;

    const isGameCompleted = game?.status === 'completed';
    const isEventCompleted = event?.status === 'completed';
    const showCompleteEventButton = isGameCompleted && !isEventCompleted;

    const totalRallies = game.rallies?.length || 0;
    const playerATotalPoints = (game.rallies || []).filter(r => r.pointWinner === 'A').length;
    const playerBTotalPoints = (game.rallies || []).filter(r => r.pointWinner === 'B').length;

    return (
      <div className="space-y-6">
        {/* Winner Banner */}
        <div className={`${theme.card} rounded-lg border ${theme.border} p-6 text-center`}>
          <Trophy className="w-16 h-16 mx-auto text-yellow-500 mb-4" />
          <h2 className={`text-2xl font-bold ${theme.text} mb-2`}>{winnerName} Wins!</h2>
          <div className={`text-lg ${theme.textMuted} mb-2`}>
            defeated <span className={theme.text}>{loserName}</span>
          </div>
          <div className={`text-xl font-semibold ${theme.text}`}>
            {game.playerAGamesWon || 0} - {game.playerBGamesWon || 0}
          </div>
          <div className={`text-sm ${theme.textMuted} mt-1`}>
            Best of {game.bestOf} | {(game.playerA?.players?.length || 0) > 1 ? 'Doubles' : 'Singles'}
          </div>
        </div>

        {/* Match Summary */}
        <div className={`${theme.card} rounded-lg border ${theme.border} p-4`}>
          <h3 className={`${theme.text} font-semibold mb-4`}>Match Summary</h3>

          <div className={`grid grid-cols-3 gap-2 mb-4 pb-3 border-b ${theme.border}`}>
            <div className={`text-center ${playerAWon ? 'font-semibold' : ''}`}>
              <div className={`${theme.text} ${playerAWon ? 'text-green-600' : ''}`}>
                {game.playerA?.name}
                {playerAWon && <Trophy className="w-4 h-4 inline ml-1 text-yellow-500" />}
              </div>
              <div className={`text-2xl font-bold ${playerAWon ? 'text-green-600' : theme.text}`}>
                {game.playerAGamesWon || 0}
              </div>
              <div className={`text-xs ${theme.textMuted}`}>games won</div>
            </div>
            <div className={`text-center ${theme.textMuted} flex items-center justify-center text-xl`}>
              VS
            </div>
            <div className={`text-center ${!playerAWon ? 'font-semibold' : ''}`}>
              <div className={`${theme.text} ${!playerAWon ? 'text-green-600' : ''}`}>
                {game.playerB?.name}
                {!playerAWon && <Trophy className="w-4 h-4 inline ml-1 text-yellow-500" />}
              </div>
              <div className={`text-2xl font-bold ${!playerAWon ? 'text-green-600' : theme.text}`}>
                {game.playerBGamesWon || 0}
              </div>
              <div className={`text-xs ${theme.textMuted}`}>games won</div>
            </div>
          </div>

          <div className={`grid grid-cols-3 gap-2 mb-4 pb-3 border-b ${theme.border}`}>
            <div className={`text-center text-lg font-semibold ${theme.text}`}>
              {playerATotalPoints}
            </div>
            <div className={`text-center text-sm ${theme.textMuted}`}>
              Total Points
            </div>
            <div className={`text-center text-lg font-semibold ${theme.text}`}>
              {playerBTotalPoints}
            </div>
          </div>

          <h4 className={`${theme.textMuted} text-sm font-medium mb-3`}>Game-by-Game Scores</h4>
          <div className="space-y-2">
            {(game.matches || []).map((match) => {
              const gameWinnerIsA = match.winnerPlayer === 'A';
              return (
                <div
                  key={match.id || match.gameNumber}
                  className={`grid grid-cols-3 gap-2 p-3 rounded-lg ${theme.backgroundSecondary}`}
                >
                  <div className={`text-center font-semibold ${gameWinnerIsA ? 'text-green-600' : theme.text}`}>
                    {match.playerAScore}
                  </div>
                  <div className={`text-center text-sm ${theme.textMuted}`}>
                    Game {match.gameNumber}
                  </div>
                  <div className={`text-center font-semibold ${!gameWinnerIsA ? 'text-green-600' : theme.text}`}>
                    {match.playerBScore}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Rally History */}
        {totalRallies > 0 && (
          <div className={`${theme.card} rounded-lg border ${theme.border} p-4`}>
            <h3 className={`${theme.text} font-semibold mb-4`}>
              Point History ({totalRallies} total points)
            </h3>

            <div className="space-y-3">
              {(game.matches || []).map((match) => {
                const matchRallies = (game.rallies || []).filter(r => r.gameNumber === match.gameNumber);
                if (matchRallies.length === 0) return null;

                const gameWinnerIsA = match.winnerPlayer === 'A';
                const gameWinnerName = gameWinnerIsA ? game.playerA?.name : game.playerB?.name;

                return (
                  <details
                    key={match.gameNumber}
                    className={`${theme.backgroundSecondary} rounded-lg border ${theme.border}`}
                    open={match.gameNumber === (game.matches?.length || 1)}
                  >
                    <summary className="p-3 cursor-pointer flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={`font-medium ${theme.text}`}>Game {match.gameNumber}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${gameWinnerIsA ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                          {gameWinnerName} won
                        </span>
                      </div>
                      <div className={`font-semibold ${theme.text}`}>
                        <span className={gameWinnerIsA ? 'text-green-600' : ''}>{match.playerAScore}</span>
                        <span className={theme.textMuted}> - </span>
                        <span className={!gameWinnerIsA ? 'text-green-600' : ''}>{match.playerBScore}</span>
                      </div>
                    </summary>

                    <div className="px-3 pb-3">
                      {renderRallyHeaders()}
                      <div className="max-h-40 overflow-y-auto">
                        {renderRallyRows(matchRallies)}
                      </div>
                      <div className={`mt-2 pt-2 border-t ${theme.border} text-xs ${theme.textMuted} flex justify-between`}>
                        <span>{matchRallies.length} points played</span>
                        <span>
                          {matchRallies.filter(r => r.pointWinner === 'A').length} - {matchRallies.filter(r => r.pointWinner === 'B').length} points
                        </span>
                      </div>
                    </div>
                  </details>
                );
              })}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="space-y-3">
          {isScorer && showCompleteEventButton && (
            <button
              onClick={handleCompleteEvent}
              className="w-full bg-green-600 hover:bg-green-700 text-white py-4 rounded-lg font-semibold text-lg flex items-center justify-center gap-2"
            >
              <Check className="w-5 h-5" />
              Complete Event & Continue
            </button>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${theme.background} flex items-center justify-center`}>
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className={`${theme.background} p-4`}>
      <div className="flex items-center justify-between mb-6">
        <h1 className={`text-xl font-bold ${theme.text}`}>
          Badminton
        </h1>
        <div className="flex items-center gap-3">
          <button
            onClick={handleRefresh}
            disabled={saving}
            title="Refresh score"
            className={`${theme.textMuted} disabled:opacity-40`}
          >
            <RotateCcw className="w-5 h-5" />
          </button>
          {isScorer && currentPhase !== 'completed' && (
            <button
              onClick={handleResetMatch}
              disabled={saving || paused}
              title="Reset match"
              className="text-red-400 disabled:opacity-40"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {currentPhase === 'scoring' && completedGameSummary
        ? renderBetweenGamesScreen()
        : currentPhase === 'scoring'
          ? renderScoringPhase()
          : renderCompletedPhase()
      }

      {confirmModal.isOpen && (
        <ConfirmModal
          isOpen={confirmModal.isOpen}
          title={confirmModal.title}
          message={confirmModal.message}
          confirmText={confirmModal.confirmText}
          onConfirm={confirmModal.onConfirm}
          onClose={confirmModal.onCancel}
        />
      )}

    </div>
  );
};

export default BadmintonScoring;
